import React from 'react';
import { useParams } from 'react-router-dom';

import { StyleSheet, Text, View } from './components/base_components';
import { useBusy, BusyOverlay } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import BoxPropList from './components/box_prop_list';
import ProgressSpinner from './components/progress_spinner';
import PropList from './components/prop_list';
import StandardBoxList from './components/standard_box_list';
import TextButton from './components/buttons/text_button';

import DeviceStore from './stores/device_store';
import OrgStore from './stores/org_store';
import PlanStore from './stores/plan_store';
import S3BucketStore from './stores/s3_bucket_store';
import UserAdminStore from './stores/user_admin_store';
import VolumeStore from './stores/volume_store';

import { relativeDate, byteFormat } from './tools/util';

const styles = StyleSheet.create({
  orgScreen: {
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  text: {
    marginLeft: '2rem',
    marginTop: '1.5em',
    fontSize: '1.5em',
  },
  button: {
    marginLeft: '1rem',
  },
  bottomLine: {
    alignSelf: 'stretch',
    height: '5rem',
    backgroundColor: 'var(--box-header-bg-color)',
    borderTopWidth: 1,
    borderColor: 'var(--box-inner-line-color)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
});

export default function OrgScreen(props) {
  const params = useParams();
  const org_id = parseInt(params.org_id ?? '0', 10) || 0;
  const org = OrgStore.useOrg(org_id);
  const device_list = DeviceStore.useOrgList(org_id);
  const volume_list = VolumeStore.useOrgList(org_id);
  const user_list = UserAdminStore.useList(org_id);
  const bucket_list = S3BucketStore.useOrgList(org_id);
  const [updates, setUpdates] = React.useState({});
  const [email, setEmail] = React.useState('');
  const [is_busy, setBusy, clearBusy] = useBusy();

  React.useEffect(() => {
    OrgStore.fetch();
  }, []);
  React.useEffect(() => {
    DeviceStore.fetchOrg(org_id);
    VolumeStore.fetchOrg(org_id);
    S3BucketStore.fetchOrg(org_id);
    UserAdminStore.fetch(org_id);
    PlanStore.fetch();
  }, [org_id]);

  function _onSave(complete) {
    if (Object.keys(updates).length > 0 && setBusy()) {
      const opts = { org_id, ...updates };
      OrgStore.updateOrg(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Save failed, please try again.');
        } else {
          complete?.();
        }
      });
    }
  }
  function _onAddUser() {
    if (email && setBusy()) {
      const opts = { org_id, email, is_admin: true };
      UserAdminStore.addUser(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Add failed, please try again.');
        } else {
          setEmail('');
        }
      });
    }
  }

  let content: any = null;
  if (!org) {
    content = <ProgressSpinner />;
  }
  return (
    <View style={styles.orgScreen}>
      <Breadcrumb
        list={[
          { text: 'Org List', url: '/org' },
          { text: org?.org_name ?? '' },
        ]}
      />
      <BoxPropList
        title='Organization Details'
        editable={true}
        updates={updates}
        onChange={setUpdates}
        onSave={_onSave}
        columns={[
          [
            {
              key: 'org_name',
              label: 'Organization Name',
              value: org?.org_name,
              input: {
                placeholder: 'org name',
              },
            },
            {
              key: 'domain_prefix',
              label: 'Custom Domain',
              value: org?.domain_prefix,
              emptyValue: '-',
              input: {
                placeholder: 'domain',
              },
            },
            {
              key: 'plan_id',
              label: 'Plan',
              value: org?.plan_id,
              emptyValue: '-',
              plan: {},
            },
          ],
        ]}
      />
      <StandardBoxList
        title='Volumes'
        headers={[
          'Volume Name',
          'Status',
          'Live Count',
          'Live Size',
          'Total Count',
          'Total Size',
        ]}
        sizes={[2]}
        rows={volume_list?.map?.((volume) => [
          volume.volume_name,
          volume.is_disabled ? 'Disabled' : 'Enabled',
          volume.live_count,
          byteFormat(volume.live_bytes),
          volume.total_count,
          byteFormat(volume.total_bytes),
        ])}
      >
        {content}
      </StandardBoxList>
      <StandardBoxList
        title='Devices'
        headers={['Device Name', 'Last Seen', 'Version']}
        sizes={[3]}
        rows={device_list?.map?.((device) => [
          device.device_name,
          relativeDate(device.last_seen) || '-',
          device.last_version || '-',
        ])}
      >
        {content}
      </StandardBoxList>
      <StandardBoxList
        title='Users'
        headers={['User Email', 'User Name', 'Role']}
        sizes={[3, 3]}
        rows={user_list?.map?.((user) => [
          user.email,
          user.user_name || '-',
          user.is_admin ? 'Admin' : 'User',
        ])}
      >
        {content}
      </StandardBoxList>
      <StandardBoxList title='Add Admin'>
        <Text style={styles.text}>Add a new admin to organization</Text>
        <PropList
          editing={true}
          columns={[
            [
              {
                key: 'email',
                label: 'Email',
                value: email,
                input: {
                  placeholder: 'email',
                  onChangeText: setEmail,
                },
              },
            ],
          ]}
        />
        <View style={styles.bottomLine}>
          <TextButton
            style={styles.button}
            text='Add Admin User'
            onPress={_onAddUser}
          />
        </View>
      </StandardBoxList>
      <StandardBoxList
        title='S3 Buckets'
        headers={['Bucket Name', 'Priority', 'Max Size']}
        rows={bucket_list?.map?.((bucket) => [
          bucket.default_name || bucket.s3_bucket,
          bucket.priority || '-',
          bucket.max_size ? byteFormat(bucket.max_size) : '-',
        ])}
      >
        {content}
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
